import { useEffect } from "react";
import { Link } from "react-router-dom";
import AdminPageLayout from "../../../components/midpoint/admin/layout/AdminPageLayout";
import AdminPageHeader from "../../../components/midpoint/admin/layout/AdminPageHeader";
import { AdminAutomotiveNav } from "../automotive/AdminAutomotiveNav";
import {
  PLAYBOOK_AUDIENCE_LABELS,
  PLAYBOOK_STATUS_LABELS,
  statusBadgeClass,
  trackOperationsPlaybookEvent,
} from "../../../constants/automapOperationsPlaybookConstants";
import { copyPlaybookTemplate, trackPlaybookRouteClick } from "../../../services/automapOperationsPlaybookApi";
import "../../../styles/adminPlaybook.css";

export function AdminPlaybookNav({ active = "hub" }) {
  const items = [
    { key: "hub", label: "Hub", href: "/admin/playbook" },
    { key: "checklists", label: "Checklists", href: "/admin/playbook/checklists" },
    { key: "templates", label: "Templates", href: "/admin/playbook/templates" },
    { key: "dealer-onboarding", label: "Dealers", href: "/admin/playbook/dealer-onboarding" },
    { key: "inventory-quality", label: "Inventario", href: "/admin/playbook/inventory-quality" },
    { key: "lead-followup", label: "Leads", href: "/admin/playbook/lead-followup" },
    { key: "trust-safety", label: "T&S", href: "/admin/playbook/trust-safety" },
    { key: "buyer-launch", label: "Launch", href: "/admin/playbook/buyer-launch" },
  ];
  return (
    <nav className="admin-console__nav-tabs admin-playbook-nav">
      {items.map((item) => (
        <Link key={item.key} to={item.href} className={active === item.key ? "is-active" : undefined}>
          {item.label}
        </Link>
      ))}
    </nav>
  );
}

export function PlaybookShell({ active, title, description, viewEvent, children }) {
  useEffect(() => {
    if (viewEvent) trackOperationsPlaybookEvent(viewEvent);
  }, [viewEvent]);

  return (
    <AdminPageLayout>
      <AdminPageHeader eyebrow="AutoMapa · Operations Playbook" title={title} description={description} />
      <AdminAutomotiveNav active="playbook" />
      <AdminPlaybookNav active={active} />
      <main className="admin-console__main wrap admin-playbook">{children}</main>
    </AdminPageLayout>
  );
}

export function PlaybookStatusBadge({ status }) {
  return (
    <span className={statusBadgeClass(status)}>
      {PLAYBOOK_STATUS_LABELS[status] || status}
    </span>
  );
}

export function PlaybookAudienceTags({ audience = [] }) {
  if (!audience.length) return null;
  return (
    <div className="admin-playbook__tags">
      {audience.map((a) => (
        <span key={a} className="badge badge--muted">
          {PLAYBOOK_AUDIENCE_LABELS[a] || a}
        </span>
      ))}
    </div>
  );
}

export function RelatedRouteCards({ routes = [], sectionSlug }) {
  if (!routes.length) return null;
  return (
    <section className="dealer-state-box admin-playbook__routes">
      <h3>Rutas admin relacionadas</h3>
      <div className="admin-playbook__route-grid">
        {routes.map((route) => (
          <Link
            key={route.href}
            to={route.href}
            className="dealer-portal-btn"
            onClick={() => {
              trackOperationsPlaybookEvent("operations_playbook_related_route_clicked", {
                slug: sectionSlug,
                href: route.href,
              });
              trackPlaybookRouteClick(sectionSlug, route.href);
            }}
          >
            {route.label}
          </Link>
        ))}
      </div>
    </section>
  );
}

export function CopyTemplateButton({ templateId, label, text, onCopied }) {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      if (templateId) {
        await copyPlaybookTemplate(templateId).catch(() => {});
        trackOperationsPlaybookEvent("operations_playbook_template_copied", { templateId });
      }
      onCopied?.();
    } catch {
      /* clipboard unsupported */
    }
  };
  return (
    <div className="dealer-state-box admin-playbook__template">
      <strong>{label}</strong>
      <p className="muted admin-playbook__template-body">{text}</p>
      <button type="button" className="dealer-portal-btn" onClick={copy}>
        Copiar plantilla
      </button>
    </div>
  );
}

export function WorkflowSteps({ workflow = [] }) {
  if (!workflow.length) return null;
  return (
    <section className="dealer-state-box">
      <h3>Workflow paso a paso</h3>
      <ol className="admin-playbook__steps">
        {workflow.map((step) => (
          <li key={step.step}>
            <strong>
              {step.step}. {step.title}
            </strong>
            <p className="muted">{step.body}</p>
          </li>
        ))}
      </ol>
    </section>
  );
}

export function RulesList({ title, rules = [] }) {
  if (!rules.length) return null;
  return (
    <section className="dealer-state-box">
      <h3>{title}</h3>
      <ul className="dashboard-checklist">
        {rules.map((rule) => (
          <li key={rule}>{rule}</li>
        ))}
      </ul>
    </section>
  );
}

export function SectionTableOfContents({ section }) {
  if (!section) return null;
  const items = [
    { id: "when", label: "Cuándo usar" },
    { id: "workflow", label: "Workflow" },
    { id: "checklists", label: "Checklists" },
    { id: "templates", label: "Plantillas" },
    { id: "rules", label: "Reglas" },
    { id: "routes", label: "Rutas admin" },
  ];
  return (
    <nav className="admin-playbook__toc dealer-state-box">
      <h3>Contenido</h3>
      <ul>
        {items.map((item) => (
          <li key={item.id}>
            <a href={`#playbook-${item.id}`}>{item.label}</a>
          </li>
        ))}
      </ul>
    </nav>
  );
}
